import React from 'react'
import { useRouter } from 'next/router'

import {
  useConversationStore,
  useConversationMessageStore,
  useConversationLandingOnboardingStore,
} from 'stores/conversation'
import { useAuth } from 'utils/auth'
import ConversationHeader from 'app/ConversationPage/components/ConversationHeader'
import ConversationInputBox from 'app/ConversationPage/components/ConversationInputBox'
import ConversationMessage from 'app/ConversationPage/components/ConversationMessage'
import ConversationMessageError from 'app/ConversationPage/components/ConversationMessageError'
import ConversationMessageLoading from 'app/ConversationPage/components/ConversationMessageLoading'
import ConversationSuggestions from 'app/ConversationPage/components/ConversationSuggestions'
import NewSessionConfirmSheet from 'app/ConversationPage/components/NewSessionConfirmSheet'
import {
  CONVERSATION_STORAGE_KEY,
  CONVERSATION_LANDING_ONBOARDING_STORAGE_KEY,
} from 'app/ConversationPage/configs'
import useConversation from 'app/ConversationPage/hooks/useConversation'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'
import { ForbiddenError } from 'app/ConversationPage/utils/customError'
import ConversationLanding from 'app/ConversationPage/components/ConversationLanding'
import ConversationLandingCoachMark from 'app/ConversationPage/components/ConversationLanding/ConversationLandingCoachMark'
import ConversationGreeting from 'app/ConversationPage/components/ConversationLanding/ConversationGreeting'
import { IS_CONVERSATION_WITH_AI_NEW_LANDING_ENABLED } from 'configs/toggle'
import { useExperimentsContext } from 'utils/providers/useExperimentsContext'

interface IConversationPageAppProps {
  isGuruAIEligible: boolean
}
const ConversationPageApp: React.FC<IConversationPageAppProps> = ({
  isGuruAIEligible,
}) => {
  const { session } = useAuth()
  const router = useRouter()
  const [showLandingOnboardingCoachMark, setShowLandingOnboardingCoachMark] =
    React.useState(false)

  const { sendMessage, containerRef } = useConversation()
  const { hasConversationId, savedMessages, conversationId } =
    useConversationStore()
  const {
    errorBoundary,
    isConnecting,
    isStreaming,
    isConfirmSheetOpen,
    messages,
    hasMessages,
    setMessages,
    resetMessages,
    setConfirmSheetOpen,
  } = useConversationMessageStore()
  const {
    isLandingOnboardingFinished,
    landingOnboardingStep,
    setLandingOnboardingFinished,
    setLandingOnboardingVariant,
  } = useConversationLandingOnboardingStore()
  const { SETrackConversationPage } = useSETrackEvent()

  const hasConversation = hasConversationId() && hasMessages()

  // Guru AI New Landing page
  const { getExperimentByName } = useExperimentsContext()
  const experimentGuruAINewLandingPage = getExperimentByName(
    'guru_ai_new_landing_page'
  )
  const conversationLandingVariant =
    (router?.query?.CONVERSATION_LANDING_VARIANT as string) ??
    experimentGuruAINewLandingPage?.treatment ??
    'control'

  const showLandingOnboarding = React.useMemo(
    () =>
      IS_CONVERSATION_WITH_AI_NEW_LANDING_ENABLED &&
      ((conversationLandingVariant === 'variant_1' &&
        !isLandingOnboardingFinished) ||
        (conversationLandingVariant === 'variant_2' &&
          !isLandingOnboardingFinished &&
          landingOnboardingStep === 0)) &&
      !hasConversation,
    [
      conversationLandingVariant,
      hasConversation,
      isLandingOnboardingFinished,
      landingOnboardingStep,
    ]
  )

  const showLandingOnboardingVariant3 = React.useMemo(
    () =>
      IS_CONVERSATION_WITH_AI_NEW_LANDING_ENABLED &&
      conversationLandingVariant === 'variant_3',
    [conversationLandingVariant]
  )

  const setConversationStoreNameByUserId = (stores) => {
    stores.forEach(([store, storageKey]) => {
      // rename the store
      store?.persist.setOptions({
        name: `${storageKey}-${session.user.id}` as string,
      })
      // rehydrate the store
      store?.persist.rehydrate()
      // remove the default index
      localStorage.removeItem(storageKey)
    })
  }

  React.useEffect(() => {
    SETrackConversationPage('GURU_AI_PAGE', {
      event_trigger: 'HOMEPAGE',
      is_new_ai_chat_session: [!hasConversation, 'bool'],
      ai_chat_session_id: conversationId || null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // prefill the displayed "messages" from the "savedMessages" store
  React.useEffect(() => {
    if (
      session?.user?.id &&
      Object.keys(savedMessages).length > 0 &&
      Object.keys(messages).length === 0
    ) {
      setMessages(savedMessages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, savedMessages])

  // set store name unique to the user and rehydrate the store
  React.useEffect(() => {
    if (session?.user?.id) {
      setConversationStoreNameByUserId([
        [useConversationStore, CONVERSATION_STORAGE_KEY],
        [
          useConversationLandingOnboardingStore,
          CONVERSATION_LANDING_ONBOARDING_STORAGE_KEY,
        ],
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  // reset displayed "messages" when unmount
  React.useEffect(() => {
    return () => {
      resetMessages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setLandingOnboardingVariant(conversationLandingVariant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationLandingVariant])

  React.useEffect(() => {
    if (IS_CONVERSATION_WITH_AI_NEW_LANDING_ENABLED && hasConversation) {
      setLandingOnboardingFinished()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConversationId])

  React.useEffect(() => {
    if (
      IS_CONVERSATION_WITH_AI_NEW_LANDING_ENABLED &&
      conversationLandingVariant === 'variant_2' &&
      !isLandingOnboardingFinished &&
      landingOnboardingStep > 0
    ) {
      setShowLandingOnboardingCoachMark(true)
      setTimeout(() => {
        window?.scrollTo?.(0, 0)
      }, 50)
    } else {
      setShowLandingOnboardingCoachMark(false)
    }
  }, [
    conversationLandingVariant,
    isLandingOnboardingFinished,
    landingOnboardingStep,
  ])

  if (!isGuruAIEligible) {
    throw new ForbiddenError(null, new Error('User not in experiment'))
  }

  if (errorBoundary) {
    throw errorBoundary
  }

  if (showLandingOnboarding) {
    return <ConversationLanding />
  }

  return (
    <>
      <ConversationHeader showRightAction />

      <main className="relative min-h-screen overflow-x-hidden">
        <section
          ref={containerRef}
          id="conversation-body"
          aria-label="Chatbot conversation"
          className="absolute inset-0 w-full h-full pt-16 overflow-x-hidden overflow-y-auto pb-36"
          aria-live="polite"
          tabIndex={0}
        >
          {showLandingOnboardingVariant3 && <ConversationGreeting />}

          {hasConversation ? (
            <>
              {Object.values(messages).map((message, index) => {
                if (message?.status === 'error') {
                  return (
                    <ConversationMessageError
                      key={`conversation-message-error-${message.messageId}`}
                      {...message}
                      isLoading={message.type === 'server' && isConnecting}
                      isLastItem={index === Object.entries(messages).length - 1}
                    />
                  )
                }

                return (
                  <ConversationMessage
                    key={`conversation-message-${message.messageId}`}
                    mode={message.type}
                    messageTokens={message?.tokens ?? []}
                    messageId={message?.messageId ?? null}
                    index={index}
                    isLastItem={index === Object.entries(messages).length - 1}
                  />
                )
              })}

              {isConnecting && !isStreaming && <ConversationMessageLoading />}
            </>
          ) : (
            <>
              {!showLandingOnboardingVariant3 && (
                <ConversationSuggestions
                  onClick={(text, index) => {
                    setLandingOnboardingFinished()
                    sendMessage(text, index, true)
                  }}
                />
              )}
            </>
          )}
        </section>

        <ConversationInputBox />

        <NewSessionConfirmSheet
          isOpen={isConfirmSheetOpen}
          onClose={() => {
            setConfirmSheetOpen(false)
          }}
        />

        {showLandingOnboardingCoachMark && <ConversationLandingCoachMark />}
      </main>
    </>
  )
}

export default ConversationPageApp
